import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isAuthenticated } from '../utils/auth';
import { getUserPreferences, updateUserPreferences } from '../utils/userPreferences';
import { getExchangeRates, convertCurrency, convertCurrencySync } from '../utils/currencyConversion';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Supported currencies with their symbols and names
export const supportedCurrencies = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
};

export const CurrencyProvider = ({ children }) => {
  // Load from localStorage immediately (synchronous) for instant display
  const getInitialCurrency = () => {
    const savedCurrency = localStorage.getItem('currency');
    if (savedCurrency && supportedCurrencies[savedCurrency]) {
      return savedCurrency;
    }
    return 'USD';
  };
  
  const [currentCurrency, setCurrentCurrency] = useState(getInitialCurrency);
  const [loading, setLoading] = useState(false); // Start as false since we load from localStorage immediately
  const [exchangeRates, setExchangeRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);

  // Sync with database in background (non-blocking)
  useEffect(() => {
    const syncWithDatabase = async () => {
      if (!isAuthenticated()) {
        setLoading(false);
        return;
      }

      try {
        const preferences = await getUserPreferences();
        if (preferences?.currency && supportedCurrencies[preferences.currency]) {
          // Only update if different from localStorage
          if (preferences.currency !== currentCurrency) {
            setCurrentCurrency(preferences.currency);
            localStorage.setItem('currency', preferences.currency);
          }
        } else {
          // Save current localStorage value to database
          const savedCurrency = localStorage.getItem('currency') || 'USD';
          if (savedCurrency && supportedCurrencies[savedCurrency]) {
            await updateUserPreferences({ currency: savedCurrency });
          }
        }
      } catch (error) {
        console.error('Error syncing currency preference with database:', error);
        // Continue with localStorage value
      } finally {
        setLoading(false);
      }
    };

    syncWithDatabase();
  }, []); // Only run once on mount

  // Fetch exchange rates on mount and when currency changes
  useEffect(() => {
    const fetchRates = async () => {
      setRatesLoading(true);
      try {
        const rates = await getExchangeRates();
        if (rates) {
          setExchangeRates(rates);
        }
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
      } finally {
        setRatesLoading(false);
      }
    };

    fetchRates();
    
    // Refresh rates every hour
    const interval = setInterval(fetchRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const changeCurrency = async (currencyCode) => {
    if (!supportedCurrencies[currencyCode]) {
      return;
    }
    
    // Update immediately (synchronous)
    setCurrentCurrency(currencyCode);
    localStorage.setItem('currency', currencyCode);

    // Save to database in background (non-blocking)
    if (isAuthenticated()) {
      updateUserPreferences({ currency: currencyCode }).catch(error => {
        console.error('Error saving currency preference to database:', error);
        // Continue anyway - localStorage is already updated
      });
    }
    
    // Notify other components
    window.dispatchEvent(new CustomEvent('currencychange', { detail: { currency: currencyCode } }));
  };

  /**
   * Format currency amount with conversion
   * @param {number|string} amount - Original amount
   * @param {string} originalCurrency - Currency of the original amount (from ad/order)
   * @param {string} displayCurrency - Currency to display (defaults to currentCurrency)
   * @returns {string} Formatted currency string
   */
  const formatCurrency = useCallback((amount, originalCurrency = null, displayCurrency = null) => {
    const currency = displayCurrency || currentCurrency;
    const currencyData = supportedCurrencies[currency] || supportedCurrencies.USD;
    const numAmount = parseFloat(amount || 0);
    
    // If original currency is provided and different from display currency, convert
    let displayAmount = numAmount;
    if (originalCurrency && originalCurrency !== currency && exchangeRates) {
      displayAmount = convertCurrencySync(numAmount, originalCurrency, currency);
    }
    
    // Format based on currency
    if (currency === 'JPY') {
      // Japanese Yen doesn't use decimals
      return `${currencyData.symbol}${Math.round(displayAmount)}`;
    }
    
    return `${currencyData.symbol}${displayAmount.toFixed(2)}`;
  }, [currentCurrency, exchangeRates]);

  /**
   * Convert currency amount (async, uses latest rates)
   * @param {number|string} amount - Amount to convert
   * @param {string} fromCurrency - Source currency
   * @param {string} toCurrency - Target currency (defaults to currentCurrency)
   * @returns {Promise<number>} Converted amount
   */
  const convertCurrencyAmount = useCallback(async (amount, fromCurrency, toCurrency = null) => {
    const targetCurrency = toCurrency || currentCurrency;
    if (fromCurrency === targetCurrency) {
      return parseFloat(amount || 0);
    }
    
    const rates = exchangeRates || await getExchangeRates();
    return await convertCurrency(amount, fromCurrency, targetCurrency, rates);
  }, [currentCurrency, exchangeRates]);

  const getCurrencySymbol = (currencyCode = null) => {
    const currency = currencyCode || currentCurrency;
    const currencyData = supportedCurrencies[currency] || supportedCurrencies.USD;
    return currencyData.symbol;
  };

  // Convert supportedCurrencies object to array for easier use in components
  const supportedCurrenciesArray = Object.values(supportedCurrencies);

  return (
    <CurrencyContext.Provider
      value={{
        currentCurrency,
        changeCurrency,
        supportedCurrencies: supportedCurrenciesArray,
        supportedCurrenciesMap: supportedCurrencies,
        currentCurrencyData: supportedCurrencies[currentCurrency] || supportedCurrencies.USD,
        formatCurrency,
        formatCurrencyAmount: formatCurrency, // Alias for backward compatibility
        convertCurrencyAmount,
        getCurrencySymbol,
        exchangeRates,
        ratesLoading,
        loading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

